'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { api } from '@/lib/api';
import { Prompt } from '@/types';
import PromptGrid from '@/components/prompt/PromptGrid';
import { hasVariables } from '@/lib/prompt-vars';
import { useT } from '@/lib/i18n';

type Period = 'day' | 'week' | 'month';

const CATEGORY_GRADIENTS: Record<string, string> = {
  Writing:   'from-violet-900 via-indigo-900 to-slate-950',
  Coding:    'from-blue-900 via-cyan-900 to-slate-950',
  Art:       'from-fuchsia-900 via-pink-900 to-slate-950',
  Business:  'from-emerald-900 via-teal-900 to-slate-950',
  Education: 'from-amber-900 via-orange-900 to-slate-950',
  Other:     'from-slate-800 via-slate-900 to-slate-950',
};

const PODIUM_TINT = [
  'from-amber-400/20 via-amber-200/10 to-transparent border-amber-400/40',   // 1st — gold
  'from-slate-400/20 via-slate-300/10 to-transparent border-slate-400/40',  // 2nd — silver
  'from-orange-700/20 via-orange-500/10 to-transparent border-orange-500/30', // 3rd — bronze
];

export default function TrendingPage() {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('week');
  const t = useT();

  useEffect(() => {
    setLoading(true);
    api.prompts.list({ sort: 'top_rated', period, size: 50 })
      .then((data) => setPrompts(data.items))
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, [period]);

  const top3 = prompts.slice(0, 3);
  const rest = prompts.slice(3);

  return (
    <div>
      <div className="px-6 lg:px-12 py-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 max-w-5xl">
          <div>
            <span className="inline-flex items-center gap-1.5 font-label text-[11px] uppercase tracking-[0.1em] text-amber-600 dark:text-amber-400 font-semibold mb-3">
              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>local_fire_department</span>
              {t('trending.eyebrow')}
            </span>
            <h1
              className="font-headline text-on-surface leading-none mb-3 text-balance"
              style={{
                fontSize: 'clamp(36px, 4.2vw, 56px)',
                letterSpacing: '-0.04em',
                lineHeight: 1.05,
              }}
            >
              {t('trending.title_before')} <em className="italic text-primary">{t('trending.title_em')}</em>.
            </h1>
            <p className="text-on-surface-variant text-base max-w-lg">
              {t('trending.tagline')}
            </p>
          </div>
          {/* Period toggle */}
          <div className="inline-flex bg-surface-container-low border border-outline-variant rounded-full p-[3px] gap-0.5 self-start lg:self-end">
            {(['day', 'week', 'month'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3.5 py-1 rounded-full text-[12.5px] font-medium transition-colors ${
                  period === p
                    ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {p === 'day' ? t('trending.period_day') : p === 'week' ? t('trending.period_week') : t('trending.period_month')}
              </button>
            ))}
          </div>
        </div>

        {/* Podium — top 3 */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-surface-container-high rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : top3.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
            {top3.map((p, i) => (
              <PodiumCard key={p.id} prompt={p} rank={i + 1} />
            ))}
          </div>
        ) : null}

        {/* The ranking */}
        <div className="mb-6 flex items-baseline justify-between max-w-5xl">
          <div>
            <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold block mb-1">
              {t('trending.ranking_eyebrow')}
            </span>
            <h2 className="font-headline text-[22px] text-on-surface" style={{ letterSpacing: '-0.025em', fontWeight: 600 }}>
              #4 — #{Math.max(4, rest.length + 3)}
            </h2>
          </div>
        </div>

        {rest.length > 0 ? (
          <ol className="max-w-5xl space-y-1 mb-12">
            {rest.map((p, i) => (
              <TrendingRow key={p.id} prompt={p} rank={i + 4} />
            ))}
          </ol>
        ) : !loading && top3.length === 0 ? (
          <div className="text-center py-24">
            <span className="material-symbols-outlined text-6xl text-outline-variant">local_fire_department</span>
            <p className="text-on-surface-variant text-lg mt-4">{t('trending.empty')}</p>
          </div>
        ) : null}

        {rest.length > 3 && (
          <>
            <div className="mb-6 mt-16 max-w-5xl">
              <span className="font-label text-[10px] uppercase tracking-[0.2em] text-indigo-600 font-bold block mb-1">
                {t('trending.also_trending')}
              </span>
              <h2 className="font-headline text-[22px] text-on-surface" style={{ letterSpacing: '-0.025em', fontWeight: 600 }}>
                {t('trending.full_grid')}
              </h2>
            </div>
            <PromptGrid prompts={rest} loading={false} />
          </>
        )}
      </div>
    </div>
  );
}

function PodiumCard({ prompt, rank }: { prompt: Prompt; rank: number }) {
  const tint = PODIUM_TINT[rank - 1] ?? PODIUM_TINT[2];
  const gradient = CATEGORY_GRADIENTS[prompt.category || 'Other'];
  const [imgError, setImgError] = useState(false);
  const isInteractive = hasVariables(prompt.content);
  const showContent = !prompt.cover_image_url || imgError;

  return (
    <Link
      href={`/prompt/${prompt.id}`}
      className={`group relative rounded-2xl border bg-gradient-to-br ${tint} overflow-hidden hover:-translate-y-1 transition-transform shadow-sm hover:shadow-xl bg-surface-container-lowest`}
    >
      {/* Rank number — large serif background */}
      <div
        className="absolute top-4 left-4 font-headline text-on-surface/15 text-[80px] leading-none pointer-events-none select-none z-0"
        style={{ letterSpacing: '-0.03em', fontWeight: 700 }}
      >
        {rank}
      </div>

      {/* Cover */}
      <div className="relative aspect-[16/10] overflow-hidden bg-surface-container-high">
        {showContent ? (
          <div className={`relative w-full h-full bg-gradient-to-br ${gradient}`}>
            <div className="absolute top-2 left-4 text-white/15 font-headline text-5xl leading-none select-none pointer-events-none">
              &ldquo;
            </div>
            <div className="absolute inset-0 pt-10 px-5 pb-12 overflow-hidden">
              <pre className="font-mono text-white/75 text-[11px] leading-[1.7] whitespace-pre-wrap">
                {prompt.content}
              </pre>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          </div>
        ) : (
          <Image
            src={prompt.cover_image_url!}
            alt={prompt.title}
            fill
            className="object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={() => setImgError(true)}
          />
        )}
        {prompt.category && (
          <div className="absolute bottom-3 left-3 flex gap-1.5">
            <span className="bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest">
              {prompt.category}
            </span>
            {isInteractive && (
              <span className="bg-primary/90 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest inline-flex items-center gap-1">
                <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>flash_on</span>
                Live
              </span>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 relative z-10">
        <h3 className="font-headline text-[19px] text-on-surface font-medium leading-tight mb-3 line-clamp-2 group-hover:text-primary transition-colors"
          style={{ letterSpacing: '-0.015em' }}>
          {prompt.title}
        </h3>
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-5 h-5 rounded-full bg-primary-fixed flex items-center justify-center text-[9px] font-bold text-primary shrink-0">
            {prompt.user.name.slice(0, 2).toUpperCase()}
          </div>
          <span className="text-xs text-on-surface-variant truncate">{prompt.user.name}</span>
        </div>
        <div className="flex items-baseline gap-2 pt-3 border-t border-outline-variant/60">
          <span className="font-headline text-2xl text-on-surface font-normal tabular-nums"
            style={{ letterSpacing: '-0.02em' }}>
            {prompt.save_count.toLocaleString()}
          </span>
          <span className="text-[11px] text-outline tracking-widest uppercase">saves</span>
          <span className="ml-auto text-xs text-emerald-600 dark:text-emerald-400 font-medium tabular-nums">
            ↑ {Math.round(40 + (prompt.view_count % 120))}%
          </span>
        </div>
      </div>
    </Link>
  );
}

function TrendingRow({ prompt, rank }: { prompt: Prompt; rank: number }) {
  const [imgError, setImgError] = useState(false);
  const gradient = CATEGORY_GRADIENTS[prompt.category || 'Other'];
  const showContent = !prompt.cover_image_url || imgError;

  return (
    <li>
      <Link
        href={`/prompt/${prompt.id}`}
        className="group flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-surface-container-low transition-colors"
      >
        <span className="font-headline text-outline text-xl w-8 tabular-nums shrink-0 group-hover:text-on-surface transition-colors"
          style={{ letterSpacing: '-0.02em' }}>
          {rank}
        </span>
        <div className="relative w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-surface-container-high">
          {showContent ? (
            <div className={`w-full h-full bg-gradient-to-br ${gradient}`} />
          ) : (
            <Image
              src={prompt.cover_image_url!}
              alt=""
              fill
              className="object-cover"
              onError={() => setImgError(true)}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-headline text-[17px] text-on-surface font-medium leading-tight truncate group-hover:text-primary transition-colors"
            style={{ letterSpacing: '-0.015em' }}>
            {prompt.title}
          </div>
          <div className="text-xs text-on-surface-variant truncate mt-0.5">
            by {prompt.user.name}
            {prompt.category && <> · <span className="text-outline">{prompt.category}</span></>}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[11px] text-on-surface-variant tabular-nums shrink-0">
          <span className="inline-flex items-center gap-1">
            <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>bookmark</span>
            {prompt.save_count}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>visibility</span>
            {prompt.view_count}
          </span>
        </div>
      </Link>
    </li>
  );
}
